import {getTestProps} from '@benchling/js-test-utils';
import {Button, Tooltip} from '@mantine/core';
import React from 'react';

import * as NavConstants from 'constants/NavConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import {
  openExistingChat,
  openRoutineHomepageView,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AgentFoundryChatButtonProps {
  /** The foundry's Coordinator Agent (routine) id, or null if none is set. */
  coordinatorAgentId: string | null;
  /** The chat recorded as the foundry's active coordinator chat, or null if none is set. */
  activeCoordinatorChatId: string | null;
}

/**
 * "Chat" quick link on the Agent Foundry page for talking to the foundry's Coordinator.
 *
 * - No Coordinator: disabled, explaining that one has not been set.
 * - Active coordinator chat set: opens that chat in the semi-expanded sidepanel (not full-screen,
 *   which would pull focus away from the foundry page), like AgentChatButton does.
 * - Coordinator but no active chat: opens the Coordinator's agent homepage in the semi-expanded
 *   sidepanel too. The homepage is behind the routine-homepage rollout (see the
 *   `chats/agents/:routineId` route, which silently ignores the navigation when it is off), so when
 *   it is unavailable the button is disabled with an explanation rather than being a dead click.
 */
export default function AgentFoundryChatButton({
  coordinatorAgentId,
  activeCoordinatorChatId,
}: AgentFoundryChatButtonProps): JSX.Element {
  const {llmEnableRoutineHomepage} = useRolloutFlags();
  const isHomepageAvailable = isAgentsFeatureEnabled() && llmEnableRoutineHomepage;

  let disabledReason: string | null = null;
  if (coordinatorAgentId == null) {
    disabledReason = 'A coordinator agent has not been set on this foundry.';
  } else if (activeCoordinatorChatId == null && !isHomepageAvailable) {
    disabledReason =
      "This foundry has no active chat, and agent homepages are not available, so there is nowhere to open the coordinator's conversation.";
  }

  const handleClick = (): void => {
    if (activeCoordinatorChatId != null) {
      openExistingChat(activeCoordinatorChatId);
    } else if (coordinatorAgentId != null) {
      openRoutineHomepageView(coordinatorAgentId);
    }
    // Always semi-expanded (not expandSection's full-screen), so the foundry page stays visible.
    SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
  };

  return (
    <Tooltip
      label={disabledReason}
      disabled={disabledReason == null}
      multiline={true}
      width={280}
      withArrow={true}
      withinPortal={true}
    >
      <Button
        variant="secondary"
        leftIcon={<BenchlingIcon icon="sparkle" />}
        {...getDisabledTooltipButtonProps(disabledReason != null, handleClick)}
        {...getTestProps({component: 'AgentFoundryChatButton', element: 'button'})}
      >
        Chat
      </Button>
    </Tooltip>
  );
}
